/**
 * Task Inbox React Query Hooks
 * Custom hooks for inbox operations (not auto-generated).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  workTaskInboxService,
  CompleteWorkTaskInput,
  MyTasksParams,
} from '@/services/taskmanagement/WorkTaskInboxService';
import { WorkTask } from '@/types/taskmanagement/WorkTask';
import { authService } from '@/services/auth/authService';
import { worktaskKeys } from './useWorkTask';
import { useTaskInboxRealtimeConnected } from './taskInboxRealtime';

/**
 * Only reached when the push channel is down (useTaskInboxSignalR). While it is up the server
 * announces every change, so an interval would ask a question already answered — and answered
 * immediately rather than up to half a minute late.
 */
const FALLBACK_POLL_MS = 30_000;

/** Query key factory for inbox-specific queries */
export const inboxKeys = {
  all: (tenantId?: string | null) => ['task-inbox', tenantId] as const,
  myTasks: (tenantId?: string | null) => [...inboxKeys.all(tenantId), 'my-tasks'] as const,
  count: (tenantId?: string | null) => [...inboxKeys.all(tenantId), 'count'] as const,
  entityTasks: (tenantId: string | null | undefined, entityType: string, entityId: string) =>
    [...inboxKeys.all(tenantId), 'entity', entityType, entityId] as const,
};

/**
 * Fetch tasks for the current user's inbox.
 */
export function useMyTasks(params?: MyTasksParams) {
  const tenantId = authService.getTenantId();
  const realtime = useTaskInboxRealtimeConnected();

  return useQuery({
    queryKey: [...inboxKeys.myTasks(tenantId), params],
    queryFn: () => workTaskInboxService.getMyTasks(params),
    refetchInterval: realtime ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Fetch inbox count for badge.
 */
export function useInboxCount() {
  const tenantId = authService.getTenantId();
  const realtime = useTaskInboxRealtimeConnected();

  return useQuery({
    queryKey: inboxKeys.count(tenantId),
    queryFn: () => workTaskInboxService.getInboxCount(),
    refetchInterval: realtime ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Fetch tasks for a specific entity.
 */
export function useEntityTasks(entityType: string, entityId: string) {
  const tenantId = authService.getTenantId();

  return useQuery({
    queryKey: inboxKeys.entityTasks(tenantId, entityType, entityId),
    queryFn: () => workTaskInboxService.getEntityTasks(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

/** Invalidate all inbox queries including task detail. */
function useInvalidateInbox() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return () => {
    queryClient.invalidateQueries({ queryKey: inboxKeys.all(tenantId) });
    queryClient.invalidateQueries({ queryKey: worktaskKeys.all(tenantId) });
  };
}

/**
 * Claim a task.
 */
export function useClaimTask() {
  const invalidate = useInvalidateInbox();

  return useMutation<WorkTask, Error, string>({
    mutationFn: (id) => workTaskInboxService.claim(id),
    onSuccess: invalidate,
  });
}

/**
 * Unclaim (release) a task.
 */
export function useUnclaimTask() {
  const invalidate = useInvalidateInbox();

  return useMutation<WorkTask, Error, string>({
    mutationFn: (id) => workTaskInboxService.unclaim(id),
    onSuccess: invalidate,
  });
}

/**
 * Complete a task with an outcome.
 */
export function useCompleteTask() {
  const invalidate = useInvalidateInbox();

  return useMutation<WorkTask, Error, { id: string; input: CompleteWorkTaskInput }>({
    mutationFn: ({ id, input }) => workTaskInboxService.complete(id, input),
    onSuccess: invalidate,
  });
}

/**
 * Cancel a task.
 */
export function useCancelTask() {
  const invalidate = useInvalidateInbox();

  return useMutation<WorkTask, Error, string>({
    mutationFn: (id) => workTaskInboxService.cancel(id),
    onSuccess: invalidate,
  });
}
